import { Button, Grid, IconButton, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartialLabelData } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import TitledDrawer from '../../../common/ui/TitledDrawer';
import { OutlinedIconButton } from '../../../common/ui/OutlinedIconButton';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { PartialLabelEditDialog } from './PartialLabelEditDialog';

type PartialLabelListDrawerProps = {
  items: PartialLabelData[];
  onChange: (items: PartialLabelData[]) => void;
  title: string;
  subtitle?: string;
  addButtonText: string;
  emptyText: string;
  dialogTitle: string;
  fieldLabel: string;
  disabled?: boolean;
};

export const PartialLabelListDrawer = ({
  items,
  onChange,
  title,
  subtitle,
  addButtonText,
  emptyText,
  dialogTitle,
  fieldLabel,
  disabled,
}: PartialLabelListDrawerProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleSave = (item: PartialLabelData) => {
    if (editingIndex === null) {
      onChange([...items, item]);
    } else {
      const updated = [...items];
      updated[editingIndex] = item;
      onChange(updated);
    }
  };

  const handleDelete = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const editingItem = editingIndex !== null ? items[editingIndex] : null;

  const getDisplayText = (item: PartialLabelData): string => {
    return item?.[language] || item?.en || item?.he || '';
  };

  return (
    <>
      <SettingsRow label={title}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          gap={2}
          sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px', maxWidth: 520 }}>
          <Grid item sx={{ minWidth: 0 }}>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {subtitle}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {items.length} {items.length === 1 ? t('common.item') : t('common.items')}
            </Typography>
          </Grid>
          <Grid item>
            <OutlinedIconButton aria-label={title} onClick={() => setDrawerOpen(true)} disabled={disabled}>
              <Edit />
            </OutlinedIconButton>
          </Grid>
        </Grid>
      </SettingsRow>

      <TitledDrawer title={title} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Grid
          container
          flexDirection="column"
          flexWrap="nowrap"
          gap={1.5}
          flex={1}
          minHeight={0}
          sx={{ overflowY: 'auto', pt: { xs: '12px', md: '18px' }, pb: 2, paddingInline: { xs: '12px', md: '24px' } }}>
          {items.map((item, index) => (
            <Grid
              key={index}
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1, paddingInlineStart: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
              <Grid item xs={9}>
                <Typography variant="body2">{getDisplayText(item)}</Typography>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={() => handleEdit(index)} disabled={disabled}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(index)} disabled={disabled}>
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          {items.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              {emptyText}
            </Typography>
          )}

          <Button startIcon={<Add />} onClick={handleAdd} disabled={disabled} sx={{ alignSelf: 'flex-start' }}>
            {addButtonText}
          </Button>
        </Grid>
      </TitledDrawer>

      <PartialLabelEditDialog
        open={dialogOpen}
        onClose={handleClose}
        onSave={handleSave}
        item={editingItem}
        title={dialogTitle}
        fieldLabel={fieldLabel}
      />
    </>
  );
};
